import { Stack } from '@understory-io/leaf/stack';
import { css } from '@understory-io/leaf/css';
import { PaymentsNotReadyState } from '~/ui/payments-not-ready-state';

/**
 * The chrome both tablet+ booking panels sit in: the sticky `<aside>` card plus
 * the payments-not-ready gate that replaces the body when checkout isn't
 * reachable (the host cannot receive payments yet and we're not in preview).
 *
 * Only the frame is shared — the bodies are two separate components
 * (`<BookingPanelScheduled>` for experiences that sell fixed events,
 * `<BookingPanelOpenTimeSlot>` for operating-hours ones), since a date/timeslot
 * picker and a duration/tickets picker have nothing in common below this card.
 */
export function BookingPanelCard({
  canProceedToCheckout = true,
  children,
}: {
  /** When false, checkout isn't reachable — the card shows
   *  PaymentsNotReadyState instead of the panel body (mirrors the legacy
   *  NO_PAYMENT_METHODS precedence: it wins over every other state). */
  canProceedToCheckout?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Stack as="aside" gap="medium" className={cardStyles}>
      {canProceedToCheckout ? children : <PaymentsNotReadyState />}
    </Stack>
  );
}

const cardStyles = css({
  background: 'surface.raised',
  padding: 'large',
  borderRadius: 'xlarge',
  shadow: 'large',
  position: 'sticky',
  top: 'large',
  // Floor from the design (US-10453). Only the short shapes of the panel reach
  // for it — chiefly the one-off widget, whose single time slot otherwise
  // leaves the card stunted next to the calendar's. No token matches, so the
  // literal is spelled out.
  minHeight: '[300px]',
});
